import React, { useEffect, useRef, useState } from 'react';
import { string, number, func, bool } from 'prop-types';
import css from './PayPalButton.module.css';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

/**
 * PayPalButton — renders the PayPal checkout button by loading the
 * PayPal JS SDK from CDN on mount.
 *
 * Flow:
 *   1. Mounts → loads PayPal JS SDK
 *   2. Renders the PayPal Smart Button
 *   3. onClick → calls /api/paypal/create-order → returns orderId
 *   4. Buyer approves on PayPal hosted page
 *   5. onApprove → calls /api/paypal/capture-order → initiates Sharetribe tx
 *
 * @param {string}   props.listingId
 * @param {number}   [props.quantity]
 * @param {string}   [props.currency]
 * @param {Function} props.onSuccess - called with Sharetribe transaction data
 * @param {Function} [props.onError]
 */
const PayPalButton = ({
  listingId,
  quantity,
  currency,
  onSuccess,
  onError,
  processAlias,
  transition,
  protectedData,
}) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [sdkError, setSdkError] = useState(null);

  useEffect(() => {
    if (!PAYPAL_CLIENT_ID) {
      setSdkError('PayPal Client ID not configured');
      setLoading(false);
      return;
    }

    // Load PayPal SDK
    const scriptId = 'paypal-sdk';
    const existing = document.getElementById(scriptId);
    const curr = currency || 'EUR';

    const renderButton = () => {
      if (!window.paypal || !containerRef.current) return;
      // Clear previous renders
      containerRef.current.innerHTML = '';

      window.paypal
        .Buttons({
          style: { layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' },

          createOrder: async () => {
            const res = await fetch('/api/paypal/create-order', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ listingId, quantity }),
            });
            if (!res.ok) throw new Error('Failed to create PayPal order');
            const { orderId } = await res.json();
            return orderId;
          },

          onApprove: async (data) => {
            const res = await fetch('/api/paypal/capture-order', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                orderId: data.orderID,
                listingId,
                quantity,
                processAlias,
                transition,
                protectedData: {
                  ...protectedData,
                  orderDetails: { orderID: data.orderID, payerID: data.payerID },
                },
              }),
            });
            if (!res.ok) throw new Error('Failed to capture PayPal order');
            const result = await res.json();
            onSuccess && onSuccess(result);
          },

          onError: (err) => {
            console.error('[paypal] button error:', err);
            onError && onError(err);
          },
        })
        .render(containerRef.current);

      setLoading(false);
    };

    if (existing && window.paypal) {
      renderButton();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=${curr}`;
    script.async = true;
    script.onload = renderButton;
    script.onerror = () => {
      setSdkError('Failed to load PayPal SDK');
      setLoading(false);
    };
    document.head.appendChild(script);

    return () => {};
  }, [listingId, quantity]); // eslint-disable-line

  if (sdkError) return <p className={css.error}>{sdkError}</p>;

  return (
    <div className={css.root}>
      {loading && <div className={css.loading}>Loading PayPal…</div>}
      <div ref={containerRef} className={css.buttonContainer} />
    </div>
  );
};

PayPalButton.defaultProps = {
  quantity: 1,
  currency: 'EUR',
  onSuccess: null,
  onError: null,
  processAlias: 'default-purchase/release-1',
  transition: 'transition/request-payment',
  protectedData: {},
};

PayPalButton.propTypes = {
  listingId: string.isRequired,
  quantity: number,
  currency: string,
  onSuccess: func,
  onError: func,
  processAlias: string,
  transition: string,
};

export default PayPalButton;
